import React, { useState } from 'react';

interface DiceRollerProps {
  onRoll?: (dice1: number, dice2: number, total: number) => void;
  lastRoll?: { dice1: number; dice2: number; total: number; roller?: string } | null;
}

const DiceRoller: React.FC<DiceRollerProps> = ({ onRoll, lastRoll }) => {
  const [rolling, setRolling] = useState(false);

  const handleRoll = () => {
    if (rolling) return;

    setRolling(true);

    // Simulate rolling animation
    setTimeout(() => {
      const dice1 = Math.floor(Math.random() * 8) + 1;
      const dice2 = Math.floor(Math.random() * 8) + 1;
      const total = dice1 + dice2;

      onRoll?.(dice1, dice2, total);
      setRolling(false);
    }, 500);
  };

  const renderDie = (value: number | null, isRolling: boolean) => {
    const dim = isRolling || value === null;
    const display = dim ? '?' : String(value);

    return (
      <div className="w-16 h-16 transform transition-transform hover:scale-105">
        <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-lg">
          {/* Octahedron (d8) seen face-on: a large equilateral front face with
              three small adjacent faces showing as slivers along each edge.
              Corners are slightly rounded via quadratic joins. */}
          <path
            d="M 47.2 14.8 Q 50 12 52.8 14.8 L 72.2 34.2 Q 75 37 76.0 40.9 L 83.0 67.1 Q 84 71 80.1 72.0 L 53.9 79.0 Q 50 80 46.1 79.0 L 19.9 72.0 Q 16 71 17.0 67.1 L 24.0 40.9 Q 25 37 27.8 34.2 Z"
            fill="white"
            stroke={dim ? '#9ca3af' : '#1f2937'}
            strokeWidth="4"
            strokeLinejoin="round"
          />
          {/* central equilateral face */}
          <polygon
            points="50,12 84,71 16,71"
            fill="none"
            stroke={dim ? '#d1d5db' : '#6b7280'}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <text
            x="50"
            y="50"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="28"
            fontWeight="bold"
            fill={dim ? '#9ca3af' : '#111827'}
            className={dim ? 'animate-pulse' : ''}
          >
            {display}
          </text>
        </svg>
      </div>
    );
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">Dice Roller</h3>

      {/* Dice Display */}
      <div className="flex gap-4 justify-center mb-4">
        {renderDie(lastRoll?.dice1 ?? null, rolling)}
        {renderDie(lastRoll?.dice2 ?? null, rolling)}
      </div>

      {/* Total Display */}
      <div className="text-center mb-4">
        <div className="text-2xl font-bold text-purple-400">
          Total: {rolling || !lastRoll ? '?' : lastRoll.total}
        </div>
      </div>

      {/* Roll Button */}
      <button
        onClick={handleRoll}
        disabled={rolling}
        className={`w-full px-4 py-3 rounded-lg font-semibold text-white transition-all ${
          rolling
            ? 'bg-gray-600 cursor-not-allowed'
            : 'bg-purple-600 hover:bg-purple-700 active:scale-95'
        }`}
      >
        {rolling ? 'Rolling...' : 'Roll 2d8'}
      </button>
    </div>
  );
};

export default DiceRoller;
